import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Typography,
  Chip,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Search,
  Dashboard,
  Analytics,
  ContentPaste,
  CalendarToday,
  Settings,
  Person,
  Business,
  Close,
  Keyboard
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const GlobalSearch = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef(null);

  const searchItems = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      description: 'Main dashboard overview',
      icon: <Dashboard />,
      path: '/',
      category: 'Navigation'
    },
    {
      id: 'analytics',
      title: 'Analytics',
      description: 'View performance metrics and insights',
      icon: <Analytics />,
      path: '/analytics',
      category: 'Navigation'
    },
    {
      id: 'content',
      title: 'Content',
      description: 'Create and manage content',
      icon: <ContentPaste />,
      path: '/content',
      category: 'Navigation'
    },
    {
      id: 'calendar',
      title: 'Calendar',
      description: 'Schedule and manage posts',
      icon: <CalendarToday />,
      path: '/calendar',
      category: 'Navigation'
    },
    {
      id: 'ai-insights',
      title: 'AI Insights',
      description: 'AI-powered recommendations',
      icon: <Analytics />,
      path: '/ai-insights',
      category: 'Features'
    },
    {
      id: 'lead-capture',
      title: 'Lead Generation',
      description: 'Capture and manage leads',
      icon: <Person />,
      path: '/lead-capture',
      category: 'Features'
    },
    {
      id: 'settings',
      title: 'Settings',
      description: 'Account and platform settings',
      icon: <Settings />,
      path: '/settings',
      category: 'System'
    },
    {
      id: 'platform-connector',
      title: 'Platform Connector',
      description: 'Connect social media accounts',
      icon: <Business />,
      path: '/platform-connector',
      category: 'System'
    }
  ];

  const filteredItems = searchItems.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (open) {
      setSelectedIndex(0);
      setSearchTerm('');
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < filteredItems.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev > 0 ? prev - 1 : filteredItems.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredItems[selectedIndex]) {
            handleItemClick(filteredItems[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, selectedIndex, filteredItems, onClose]);

  const handleItemClick = useCallback((item) => {
    navigate(item.path);
    onClose();
  }, [navigate, onClose]);

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Navigation':
        return 'primary';
      case 'Features':
        return 'success';
      case 'System':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'var(--background-card)',
          borderRadius: '12px',
          border: '1px solid var(--border-color)',
          boxShadow: 'var(--shadow-lg)',
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h6" sx={{ color: 'var(--text-primary)' }}>
          Search
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={<Keyboard />}
            label="⌘K"
            size="small"
            sx={{ 
              backgroundColor: 'var(--background-elevated)',
              color: 'var(--text-secondary)',
              fontSize: '12px'
            }}
          />
          <IconButton
            onClick={onClose}
            sx={{ 
              color: 'var(--text-muted)',
              '&:hover': {
                color: 'var(--text-secondary)'
              }
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 0 }}>
        <TextField
          ref={searchInputRef}
          fullWidth
          placeholder="Search for pages, features, or settings..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'var(--text-muted)' }} />
              </InputAdornment>
            )
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'var(--background-elevated)',
              borderColor: 'var(--border-color)',
              '&:hover': {
                borderColor: 'var(--text-secondary)'
              },
              '&.Mui-focused': {
                borderColor: 'var(--primary-color)'
              }
            },
            '& .MuiInputBase-input': {
              color: 'var(--text-primary)'
            }
          }}
        />
        
        {filteredItems.length > 0 ? (
          <List sx={{ p: 0 }}>
            {filteredItems.map((item, index) => (
              <ListItem key={item.id} sx={{ p: 0, mb: 0.5 }}>
                <ListItemButton
                  selected={index === selectedIndex}
                  onClick={() => handleItemClick(item)}
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: index === selectedIndex ? 'var(--background-elevated)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'var(--background-elevated)'
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'var(--background-elevated)',
                      '&:hover': {
                        backgroundColor: 'var(--background-elevated)'
                      }
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: 'var(--text-secondary)' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                        {item.title}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                          {item.description}
                        </Typography>
                        <Chip
                          label={item.category}
                          size="small"
                          color={getCategoryColor(item.category)}
                          sx={{ 
                            height: 20,
                            fontSize: '10px',
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" sx={{ color: 'var(--text-muted)' }}>
              No results found for "{searchTerm}"
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--text-muted)', mt: 1 }}>
              Try searching for something else
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;
